import { useEffect, useState } from 'react';
import { LOCALES } from '@/lib/i18n';
import { api } from '@/lib/api';

interface TenantLocales {
  locales?: string[];
  defaultLocale?: string;
}

/** The shipped pair, used until the tenant's own answer arrives. */
const SHIPPED = Object.keys(LOCALES);

let cache: { locales: string[]; defaultLocale: string } | null = null;
let inflight: Promise<void> | null = null;

/**
 * §25 — which languages this tenant publishes in, and which one is the base.
 *
 * Read once per page load and shared: every catalog form needs the same answer,
 * and asking per dialog would be a request each time someone opens an item.
 *
 * A user without `settings.view` still translates — they just cannot see or
 * change which languages exist — so a 403 degrades to the shipped pair rather
 * than leaving the editor with no languages at all.
 */
export function useTenantLocales() {
  const [state, setState] = useState(
    cache ?? { locales: SHIPPED, defaultLocale: 'en' },
  );

  useEffect(() => {
    if (cache) return;
    inflight ??= api
      .get<TenantLocales>('/tenants/locales')
      .then(({ data }) => {
        cache = {
          locales: data.locales?.length ? data.locales : SHIPPED,
          defaultLocale: data.defaultLocale ?? 'en',
        };
      })
      .catch(() => {
        cache = { locales: SHIPPED, defaultLocale: 'en' };
      })
      .finally(() => {
        inflight = null;
      });

    let alive = true;
    void inflight?.then(() => {
      if (alive && cache) setState(cache);
    });
    return () => {
      alive = false;
    };
  }, []);

  // The base locale leads, so it is the tab the editor opens on.
  const ordered = [
    state.defaultLocale,
    ...state.locales.filter((code) => code !== state.defaultLocale),
  ].filter(Boolean);

  return { locales: ordered, defaultLocale: state.defaultLocale };
}
